import { DOCUMENT, NgTemplateOutlet } from '@angular/common';
import { Component, DestroyRef, inject, input, output, signal } from '@angular/core';
import { OverlayModule } from '@angular/cdk/overlay';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule, IconModule } from '@hglobal/common';
import { environment } from '@fb/environments';
import { TranslateInPipe } from '@fb/pipes/translate-in.pipe';
import {
  FloatingButtonAction,
  FloatingButtonActionType,
  SupportCategories,
} from '@fb/models';

import { SupportCircleImageComponent } from '../support-circle-image/support-circle-image.component';
import { SupportFormDialog } from '../support-form/support-form.dialog';
import { FinanceDialog } from '../finance-dialog/finance.dialog';
import { ItSupportDialog } from '../it-support-dialog/it-support.dialog';
import { AiChatFormDialog } from '../ai-chat-form/ai-chat-form.dialog';
import { AiChatFormService } from '../ai-chat-form/ai-chat-form.service';
import { AiChatTabBridgeService } from '../ai-chat-form/ai-chat-tab-bridge.service';
import { AiChatInitialAction } from '../ai-chat-form/ai-chat.types';
import { ctaToInitialAction } from '../ai-chat-form/helpers/cta-to-initial-action';
import { CtaCooldownService, CtaDefinition, PageCtaComponent } from '../cta';

@Component({
  selector: 'floating-button',
  imports: [
    SupportCircleImageComponent,
    OverlayModule,
    TranslateModule,
    IconModule,
    ButtonModule,
    TranslateInPipe,
    NgTemplateOutlet,
    PageCtaComponent,
  ],
  providers: [
    SupportFormDialog,
    FinanceDialog,
    ItSupportDialog,
    AiChatFormDialog,
    AiChatFormService,
    AiChatTabBridgeService,
  ],
  templateUrl: './floating-button.component.html',
  styleUrl: './floating-button.component.scss',
})
export class FloatingButtonComponent {
  #document = inject(DOCUMENT);
  #supportFormDialog = inject(SupportFormDialog);
  #itSupportFormDialog = inject(ItSupportDialog);
  #financeDialog = inject(FinanceDialog);
  #aiChatFormService = inject(AiChatFormService);
  #aiChatDialog = inject(AiChatFormDialog);
  #ctaCooldown = inject(CtaCooldownService);
  #destroyRef = inject(DestroyRef);

  categories = SupportCategories;
  actionType = FloatingButtonActionType;
  #dryRequestCompleted = false;

  blockBotButton = input<boolean>(false);
  actions = input.required<FloatingButtonAction[]>();
  popupOpened = output<boolean>();

  overlayOpened = false;
  aiChatButtonIcon = `${environment.shortName}-ai-chat-button`;
  protected readonly isPopupOpen = signal(false);

  openTrustLine(): void {
    this.overlayOpened = false;
    const win = this.#document.defaultView;

    win?.open(
      'https://docs.google.com/forms/d/e/1FAIpQLSeRKyuGyiGAdXU_m8W_ECAR8rSxzjSk8RUtfheI8UxClV-tjA/viewform',
      '_blank',
    );
  }

  openAiChat(initialAction?: AiChatInitialAction): void {
    this.overlayOpened = false;
    this.#openChatDialog(initialAction);
  }

  openSupport(category: SupportCategories): void {
    this.isPopupOpen.set(true);
    this.popupOpened.emit(true);
    this.#supportFormDialog
      .open(category)
      .afterClosed()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        this.isPopupOpen.set(false);
        this.popupOpened.emit(false);
      });
  }

  openFinanceDialog(): void {
    this.isPopupOpen.set(true);
    this.popupOpened.emit(true);
    this.#financeDialog
      .open()
      .afterClosed()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        this.isPopupOpen.set(false);
        this.popupOpened.emit(false);
      });
  }

  openITSupportDialog(): void {
    this.isPopupOpen.set(true);
    this.popupOpened.emit(true);
    this.#itSupportFormDialog
      .open()
      .afterClosed()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        this.isPopupOpen.set(false);
        this.popupOpened.emit(false);
      });
  }

  openOverlay(): void {
    this.overlayOpened = !this.overlayOpened;

    if (!this.#dryRequestCompleted && !this.blockBotButton()) {
      this.#aiChatFormService
        .dryRequest()
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe(() => {
          this.#dryRequestCompleted = true;
        });
    }
  }

  protected onCtaSelected(cta: CtaDefinition): void {
    this.#ctaCooldown.start(cta);
    this.openAiChat(ctaToInitialAction(cta, () => this.#ctaCooldown.cancel(cta.id)));
  }

  #openChatDialog(initialAction?: AiChatInitialAction): void {
    if (this.isPopupOpen()) {
      return;
    }

    this.isPopupOpen.set(true);
    this.popupOpened.emit(true);
    this.#aiChatDialog
      .open(initialAction ? { initialAction } : undefined)
      .afterClosed()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        this.isPopupOpen.set(false);
        this.popupOpened.emit(false);
      });
  }
}
